<template>
  <SkeletonDataTable
    :id="id"
    v-model:expanded-rows="expandedRows"
    :value="internalRateQuotes"
    :loading="loading"
    :number-of-rows="5"
    :row-class="getRowClass"
    class="rate-response-table elevated"
    collapsed-row-icon="pi pi-chevron-down"
    expanded-row-icon="pi pi-chevron-up"
    tabindex="-1"
    scrollable
    @row-expand="onRowExpand($event.data)"
  >
    <template v-if="slots.empty" #empty>
      <slot name="empty" />
    </template>

    <Column
      :header="t('carriers.carriers')"
      class="carrier-column"
      field="carrier.scac"
    >
      <template #body="{ data }: DataTableRow">
        <RateQuoteCarrier
          :rate-quote="data"
          with-context-chat
          class="carrier-column-content"
        />

        <RateQuoteWarnings :rate-quote="data" @click="onChipClicked(data)" />
      </template>
    </Column>

    <Column :header="t('carriers.modes')" class="w-1" field="mode">
      <template #body="{ data }: DataTableRow">
        {{ data.mode ? translateEnum(data.mode, 'Mode') : '-' }}

        <div v-if="data.equipmentType" class="text-gray-darker sub-heading">
          {{ data.equipmentType.name }}
        </div>
      </template>
    </Column>

    <Column :header="t('rateShop.service')" class="w-1">
      <template #body="{ data }: DataTableRow">
        {{ data.serviceType ? titleCase(data.serviceType) : '-' }}
        <div class="text-gray-darker sub-heading">
          {{ renderServiceInfo(data) }}
        </div>
      </template>
    </Column>

    <Column :header="t('rateShop.transit')" class="w-1">
      <template #body="{ data }: DataTableRow">
        {{
          data.transitTime
            ? t('rateShop.countDays', { count: data.transitTime })
            : '-'
        }}
      </template>
    </Column>

    <Column :header="t('rateShop.rateSource')" class="source-column">
      <template #body="{ data }: DataTableRow">
        <RateShopTableSource
          :rate-quote="data"
          :can-edit-rate="editable && data.source !== 'BROKERAGE'"
          :disabled="loading"
          @edit-quote="emit('edit-quote', $event)"
          @edit-spot-quote="emit('edit-spot-quote', $event)"
        />
      </template>
    </Column>

    <Column
      :header="t('common.totalParam', { param: t('rateShop.price') })"
      field="totalPrice"
      class="price-column w-full text-right"
      align-frozen="right"
      frozen
    >
      <template #body="{ data }: DataTableRow">
        <span :class="priceClass">
          {{
            data.rateStatus === 'ERROR'
              ? '-'
              : renderMoney(data.totalPrice, {
                  currencyCode: data.currencyCode,
                })
          }}
        </span>
      </template>
    </Column>

    <!--
      Column and button are always rendered to keep columns aligned. Its
      aria-hidden and visually hidden if the user can't select a rate
    -->
    <Column class="select-rate-column" align-frozen="right" frozen>
      <template #body="{ data }: DataTableRow">
        <RygenButton
          mode="secondary"
          :label="
            data.source === 'BROKERAGE'
              ? t('brokerages.sendToBrokerage')
              : t('rateShop.select')
          "
          :aria-hidden="!canSelect"
          :class="{ 'opacity-0': !canSelect }"
          :disabled="loading || !canSelect || !isRateQuoteDispatchable(data)"
          @click="onRateSelected(data)"
        />
      </template>
    </Column>

    <Column
      :header-style="{ width: '3rem', 'min-width': '3rem' }"
      align-frozen="right"
      expander
      frozen
    />

    <template #expansion="{ data }: { data: RateQuote }">
      <RateQuoteExpandedDetails
        :active-tab-key="rateQuoteToTabKeyMap.get(data.id!)"
        :rate-quote="data"
      />
    </template>
  </SkeletonDataTable>
</template>

<script lang="ts" setup>
import { useConfirm } from 'primevue/useconfirm';
import type { ClassValue, PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useExpandedRows, useUid } from '@rygen/rygen-ui/composables';
import {
  lowerCase,
  renderMoney,
  titleCase,
  translateEnum,
} from '@rygen/rygen-ui/i18n';
import { notNull } from '@rygen/rygen-ui/util';

import { useDoNotDispatch } from '@/composables';
import { RateQuote } from '@/model';

import RateQuoteExpandedDetails, {
  TabKey,
} from '@/components/rateShop/expandedDetails/RateQuoteExpandedDetails.vue';
import RateQuoteCarrier from '@/components/rateShop/RateQuoteCarrier.vue';
import RateQuoteWarnings from '@/components/rateShop/RateQuoteWarnings.vue';
import RateShopTableSource from '@/components/rateShop/RateShopTableSource.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import SkeletonDataTable from '@rygen/rygen-ui/components/ui/SkeletonDataTable.vue';
import Column from 'primevue/column';

export type DataTableRow = { data: RateQuote; index: number };

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('rate-shop-table').uid,
  },
  rateQuotes: {
    type: Array as PropType<RateQuote[]>,
    required: true,
  },
  priceClass: {
    type: [String, Object] as PropType<ClassValue>,
    default: () => 'text-success font-medium',
  },
  /**
   * Specifies if this instance of this component allows editing the rate
   *
   * Editability rules (permissions, rate source, parent entity status, etc) are
   * handled in `RateShopTableSource`
   */
  editable: {
    type: Boolean,
    default: () => false,
  },
  selectable: {
    type: Boolean,
    default: () => false,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const { rateQuotes, priceClass, editable, selectable, loading } = toRefs(props);
const emit = defineEmits<{
  (event: 'edit-spot-quote', spotQuote: RateQuote): void;
  (event: 'edit-quote', rateQuote: RateQuote): void;
  (event: 'rate-selected', rateQuote: RateQuote): void;
  (event: 'send-to-brokerage', rateQuote: RateQuote): void;
}>();

const slots = defineSlots<{
  empty?: () => any;
}>();

const { isRateQuoteDispatchable } = useDoNotDispatch();
const { t } = useI18n();

const internalRateQuotes: Ref<RateQuote[]> = ref([]);
const confirm = useConfirm();
const { expandedRows, getRowClass } = useExpandedRows(internalRateQuotes, {
  expandIfSingleItem: false,
});

const entries: [number, TabKey | undefined][] = internalRateQuotes.value
  .map((r) => r.id)
  .filter(notNull)
  .map((rateQuoteId) => [rateQuoteId, undefined]);
const rateQuoteToTabKeyMap: Ref<Map<number, TabKey | undefined>> = ref(
  new Map(entries),
);

const canSelect = computed(() => {
  return (
    selectable.value && authorization.hasAuthority('rateShop', 'selectRate')
  );
});

const onRowExpand = (rateQuote: RateQuote) => {
  if (rateQuote.id) {
    rateQuoteToTabKeyMap.value.set(rateQuote.id, undefined);
  }
};

const onChipClicked = (rateQuote: RateQuote) => {
  const isRowExpanded = expandedRows.value.includes(rateQuote);

  if (!isRowExpanded) {
    expandedRows.value.push(rateQuote);
  }

  nextTick(() =>
    rateQuoteToTabKeyMap.value.set(rateQuote.id!, 'OTHER_DETAILS'),
  );
};

const renderServiceInfo = (rateQuote: RateQuote) => {
  return [rateQuote.serviceTypeDescription, rateQuote.service]
    .filter((x) => x)
    .map((x) => titleCase(x))
    .join(' • ');
};

const onRateSelected = (rateQuote: RateQuote) => {
  if (rateQuote.doNotDispatch) {
    confirm.require({
      group: 'rygen-confirm-dialog',
      header:
        rateQuote.source === 'BROKERAGE'
          ? t('rateShop.doNotDispatch.sendToBrokerageHeader')
          : t('rateShop.doNotDispatch.selectRateHeader'),
      message: t('rateShop.doNotDispatch.entityNameConfirmOverride', {
        entityName:
          rateQuote.source === 'BROKERAGE'
            ? lowerCase(t('brokerages.brokers'))
            : lowerCase(t('carriers.carriers')),
      }),
      accept: () => acceptSelectedRate(rateQuote),
      acceptClass: 'p-button-sm p-button-warning',
      acceptIcon: 'pi pi-check',
      acceptLabel:
        rateQuote.source === 'BROKERAGE'
          ? t('brokerages.sendToBrokerage')
          : t('rateShop.select'),
      rejectClass: 'p-button-sm p-button-text',
      rejectLabel: t('common.cancel'),
    });
  } else {
    acceptSelectedRate(rateQuote);
  }
};

const acceptSelectedRate = (rateQuote: RateQuote) => {
  if (rateQuote.source === 'BROKERAGE') {
    emit('send-to-brokerage', rateQuote);
  } else {
    emit('rate-selected', rateQuote);
  }
};

watch(
  rateQuotes,
  (newVal: RateQuote[]) => {
    internalRateQuotes.value = newVal.map((x) => new RateQuote(x));
    rateQuoteToTabKeyMap.value.clear();
  },
  { immediate: true, deep: true },
);
</script>

<style>
.rate-response-table {
  .p-datatable.p-component {
    &.p-datatable-scrollable {
      td.p-datatable-frozen-column {
        background: white;
      }
    }

    .datatable-no-header {
      .p-datatable-thead {
        display: none;
      }

      tr:last-of-type td {
        border-bottom: none;
      }
    }

    .p-datatable-table {
      .p-datatable-thead {
        .carrier-column {
          max-width: 25%;
          width: 25%;
        }

        .source-column {
          /** left/right padding = normal th padding + button padding */
          padding-inline: calc(var(--sp-2) + 0.875rem);
        }

        .select-rate-column {
          /*
            The width of the td when the Select Rate button is shown. Setting
            it explicitly keeps the column the right widht even when the button
            isn't visually shown (if table is empty, rates are unselectable, etc).
          */
          width: 105.217px;
          min-width: 105.217px;
        }
      }

      .datatable-row {
        td {
          .rate-response-table-source {
            /** match button padding so 'Contract' lines up with Spot Quote button */
            padding: 0.4375rem 0.875rem;
          }
        }

        .sub-heading {
          font-size: var(--sp-3);
        }

        .carrier-column {
          max-width: 25%;
          width: 25%;

          .carrier-column-content {
            gap: var(--sp-2);
          }

          .rate-quote-warnings {
            margin-top: var(--sp-3);
          }
        }

        .source-column {
          /*
            width of the column when the source is a button so all rows line up
          */
          width: 116.817px;
        }

        .price-column {
          .price-column-content {
            gap: var(--sp-2);
          }
        }

        .select-rate-column {
          /*
            The width of the td when the Select Rate button is shown. Setting
            it explicitly keeps the column the right widht even when the button
            isn't visually shown (if table is empty, rates are unselectable, etc).
          */
          width: 105.217px;
          min-width: 105.217px;
        }
      }

      .p-datatable-row-expansion {
        & > td {
          padding: var(--sp-3);
        }
      }
    }
  }
}
</style>
